import type { EChartsOption } from 'echarts';
import { buildValueAxis, buildAxisLabelFormatter, AXIS_LABEL_FONT_SIZE } from '../shared/axis';
import { buildGridConfig } from '../shared/grid';
import { measureTextWidth } from '../shared/measureText';
import { buildAgeXAxis } from './axis';
import type { ZeroStateRetirementChartConfig } from './types';

// Match the AMCharts placeholder's fixed ranges.
const DEFAULT_MIN_AGE = 40;
const DEFAULT_MAX_AGE = 86;
const DEFAULT_MAX_VALUE = 3_500_000;

const AXIS_LABEL_MARGIN = 8;
const NAME_GAP_BUFFER = 6;

// Empty placeholder chart (no series) shown before the Retirement Planner has data: just the
// tinted background, an Age x-axis and an abbreviated-currency y-axis at fixed ranges.
export const buildZeroStateRetirementChartOption = (config: ZeroStateRetirementChartConfig): EChartsOption => {
  const {
    theme,
    formatAmount,
    privacyMask,
    backgroundColor,
    yAxisTitle,
    xAxisTitle = 'Age',
    minAge = DEFAULT_MIN_AGE,
    maxAge = DEFAULT_MAX_AGE,
    maxValue = DEFAULT_MAX_VALUE,
  } = config;

  // Reserve room for the rotated y-axis title so it clears the widest tick label (matches the
  // main RetirementChart builder).
  const sampleLabel = buildAxisLabelFormatter({ formatAmount, privacyMask, useNumberPrefix: true })(maxValue);
  const labelWidth = measureTextWidth(sampleLabel, `${AXIS_LABEL_FONT_SIZE}px ${theme.fontFamily}`);
  const yAxisNameGap = Math.ceil(labelWidth) + AXIS_LABEL_MARGIN + NAME_GAP_BUFFER;
  const yAxisLeft = Math.ceil(labelWidth) + AXIS_LABEL_MARGIN;

  return {
    backgroundColor,
    textStyle: { fontFamily: theme.fontFamily },
    animation: false,
    grid: buildGridConfig({ yAxisLeft }),
    xAxis: buildAgeXAxis({ theme, title: xAxisTitle, min: minAge, max: maxAge }),
    yAxis: {
      ...buildValueAxis({
        theme,
        formatAmount,
        privacyMask,
        title: yAxisTitle,
        nameGap: yAxisNameGap,
        useNumberPrefix: true,
        showGridLines: true,
      }),
      min: 0,
      max: maxValue,
    },
    series: [],
  };
};
